// src/hooks/useChat.js
import { useState, useEffect } from 'react';
import { getCurrentTime, truncateTitle } from './helpers';
import {
  startChat,
  getChatHistory,
  getAllSessions,
  deleteChatSession,
  sendMessage,
} from './api';

function useChat() {
  const [query, setQuery] = useState('');
  const [selectedCitation, setSelectedCitation] = useState(null);
  const [threads, setThreads] = useState([]); // Start with an empty array
  const [activeThreadId, setActiveThreadId] = useState(null); // Start with null
  const [threadMessages, setThreadMessages] = useState({}); // Start with an empty object
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to create a new thread object
  const createThread = (
    chatId,
    title = 'New Chat',
    lastMessage = 'Start a new conversation...'
  ) => ({
    id: chatId,
    title,
    lastMessage,
    timestamp: getCurrentTime(),
    isActive: true,
  });

  // Create a new thread by calling startChat
  const handleNewThread = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await startChat();
      if (data.success) {
        const newChatId = data.data.chat_record_id;
        const newThread = createThread(newChatId);
        setThreads((prev) => [
          ...prev.map((t) => ({ ...t, isActive: false })),
          newThread,
        ]);
        setActiveThreadId(newChatId);
        setThreadMessages((prev) => ({ ...prev, [newChatId]: [] }));
      } else {
        setError('Failed to create a new chat thread.');
      }
    } catch (error) {
      console.error('Error starting new chat:', error);
      setError('Error starting new chat. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch chat history for a thread
  const handleSelectThread = async (threadId) => {
    setThreads(threads.map((t) => ({ ...t, isActive: t.id === threadId })));
    setActiveThreadId(threadId);

    try {
      const data = await getChatHistory(threadId);
      if (data.success) {
        const messages = data.data.messages
          .map((msg) => [
            {
              id: `msg${Date.now()}`,
              isUser: true,
              content: msg.user,
              timestamp: new Date(msg.timestamp).toLocaleString('en-US', {
                weekday: 'long',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
            },
            {
              id: `msg${Date.now() + 1}`,
              isUser: false,
              total_count: msg.bot?.total_count || 0,
              additional_info:
                msg.bot?.additional_info || 'No response available.',
              content: msg.bot?.query
                ? `Response to: "${msg.bot.query}"`
                : msg.bot?.additional_info || 'No response available.',
              timestamp: new Date(msg.timestamp).toLocaleString('en-US', {
                weekday: 'long',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
              ...(msg.bot?.contracts && {
                table: {
                  columns: ['Contract', 'Details'],
                  rows: msg.bot.contracts.map((contract) => [
                    contract.contract_id,
                    contract.details,
                  ]),
                },
                citations: msg.bot.contracts_included,
              }),
            },
          ])
          .flat();
        setThreadMessages((prev) => ({ ...prev, [threadId]: messages }));
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      setError('Error fetching chat history.');
    }
  };

  // Delete a thread
  const handleDeleteThread = async (threadId) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await deleteChatSession(threadId);
      if (data.success) {
        setThreads(threads.filter((t) => t.id !== threadId));
        setThreadMessages((prev) => {
          const updated = { ...prev };
          delete updated[threadId];
          return updated;
        });
        if (activeThreadId === threadId) {
          const remainingThreads = threads.filter((t) => t.id !== threadId);
          const newActiveThreadId =
            remainingThreads.length > 0 ? remainingThreads[0].id : null;
          setActiveThreadId(newActiveThreadId);
          setThreads(
            remainingThreads.map((t) => ({
              ...t,
              isActive: t.id === newActiveThreadId,
            }))
          );
          if (!newActiveThreadId) {
            await handleNewThread();
          }
        }
      } else {
        setError('Failed to delete chat thread.');
      }
    } catch (error) {
      console.error('Error deleting chat session:', error);
      setError('Error deleting chat session.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle template click
  const handleTemplateClick = async (template) => {
    if (!template.isCompatible || !activeThreadId) return;

    const userMessage = {
      id: `msg${Date.now()}`,
      isUser: true,
      content: template.sampleQuery,
      timestamp: getCurrentTime(),
    };

    setIsLoading(true);
    setError(null);
    setThreadMessages((prev) => ({
      ...prev,
      [activeThreadId]: [...(prev[activeThreadId] || []), userMessage],
    }));

    setThreads(
      threads.map((t) =>
        t.id === activeThreadId
          ? {
              ...t,
              title:
                threadMessages[activeThreadId]?.length === 0
                  ? truncateTitle(template.sampleQuery)
                  : t.title,
              lastMessage: template.sampleQuery,
              timestamp: getCurrentTime(),
            }
          : t
      )
    );

    try {
      const data = await sendMessage(template.sampleQuery, activeThreadId);
      if (data.success) {
        const botResponse = data.data.bot_response;
        const responseMessage = {
          id: `msg${Date.now() + 1}`,
          isUser: false,
          content: botResponse.query
            ? `Response to: "${botResponse.query}"`
            : botResponse.additional_info || 'No response available.',
          timestamp: getCurrentTime(),
          ...(botResponse.contracts && {
            table: {
              columns: ['Contract', 'Details'],
              rows: botResponse.contracts.map((contract) => [
                contract.contract_id,
                contract.details,
              ]),
            },
            citations: botResponse.contracts_included,
          }),
        };
        setThreadMessages((prev) => ({
          ...prev,
          [activeThreadId]: [...(prev[activeThreadId] || []), responseMessage],
        }));
      } else {
        setError('Failed to process template query.');
      }
    } catch (error) {
      console.error('Error handling template query:', error);
      const errorMessage = {
        id: `msg${Date.now() + 1}`,
        isUser: false,
        content: 'Error processing your request.',
        timestamp: getCurrentTime(),
      };
      setThreadMessages((prev) => ({
        ...prev,
        [activeThreadId]: [...(prev[activeThreadId] || []), errorMessage],
      }));
      setError('Error processing template query.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle query submission
  const handleQuerySubmit = async () => {
    if (!query.trim() || !activeThreadId) return;

    const userMessage = {
      id: `msg${Date.now()}`,
      isUser: true,
      content: query,
      timestamp: getCurrentTime(),
    };

    setIsLoading(true);
    setError(null);
    setThreadMessages((prev) => ({
      ...prev,
      [activeThreadId]: [...(prev[activeThreadId] || []), userMessage],
    }));

    setThreads(
      threads.map((t) =>
        t.id === activeThreadId
          ? {
              ...t,
              title:
                threadMessages[activeThreadId]?.length === 0
                  ? truncateTitle(query)
                  : t.title,
              lastMessage: query,
              timestamp: getCurrentTime(),
            }
          : t
      )
    );

    try {
      const data = await sendMessage(query, activeThreadId);
      if (data.success) {
        const botResponse = data.data.bot_response;
        const responseMessage = {
          id: `msg${Date.now() + 1}`,
          isUser: false,
          content: botResponse.query
            ? `Response to: "${botResponse.query}"`
            : botResponse.additional_info || 'No response available.',
          timestamp: getCurrentTime(),
          ...(botResponse.contracts && {
            table: {
              columns: ['Contract', 'Details'],
              rows: botResponse.contracts.map((contract) => [
                contract.contract_id,
                contract.details,
              ]),
            },
            citations: botResponse.contracts_included,
          }),
        };
        setThreadMessages((prev) => ({
          ...prev,
          [activeThreadId]: [...(prev[activeThreadId] || []), responseMessage],
        }));
      } else {
        setError('Failed to process query.');
      }
    } catch (error) {
      console.error('Error handling query:', error);
      const errorMessage = {
        id: `msg${Date.now() + 1}`,
        isUser: false,
        content: 'Error processing your request.',
        timestamp: getCurrentTime(),
      };
      setThreadMessages((prev) => ({
        ...prev,
        [activeThreadId]: [...(prev[activeThreadId] || []), errorMessage],
      }));
      setError('Error processing query.');
    } finally {
      setIsLoading(false);
      setQuery('');
    }
  };

  // Initialize the chat by fetching existing sessions and creating a new thread if none exist
  useEffect(() => {
    const initializeChat = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch existing sessions
        const sessionsData = await getAllSessions();
        let fetchedThreads = [];

        if (sessionsData.success && sessionsData.data.length > 0) {
          // Filter out threads with no messages
          fetchedThreads = sessionsData.data
            .filter((session) => session.messages.length > 0) // Only include threads with messages
            .map((session, index) => ({
              id: session._id,
              title: truncateTitle(session.messages[0].user),
              lastMessage: session.messages[0].user,
              timestamp: new Date(session.created_at).toLocaleString('en-US', {
                weekday: 'long',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
              isActive: false, // We'll set the active thread below
            }));

          if (fetchedThreads.length > 0) {
            // Restore the last active thread from localStorage, or default to the first thread
            const lastActiveThreadId =
              localStorage.getItem('lastActiveThreadId');
            const activeThread =
              fetchedThreads.find((t) => t.id === lastActiveThreadId) ||
              fetchedThreads[0];
            setThreads(
              fetchedThreads.map((t) => ({
                ...t,
                isActive: t.id === activeThread.id,
              }))
            );
            setActiveThreadId(activeThread.id);
            setThreadMessages((prev) => ({
              ...prev,
              [activeThread.id]: [],
            }));
          } else {
            // If no threads with messages exist, create a new one
            const startData = await startChat();
            if (startData.success) {
              const initialChatId = startData.data.chat_record_id;
              const initialThread = createThread(initialChatId);
              setThreads([initialThread]);
              setActiveThreadId(initialChatId);
              setThreadMessages({ [initialChatId]: [] });
            } else {
              setError('Failed to initialize chat.');
            }
          }
        } else {
          // If no threads exist at all, create a new one
          const startData = await startChat();
          if (startData.success) {
            const initialChatId = startData.data.chat_record_id;
            const initialThread = createThread(initialChatId);
            setThreads([initialThread]);
            setActiveThreadId(initialChatId);
            setThreadMessages({ [initialChatId]: [] });
          } else {
            setError('Failed to initialize chat.');
          }
        }
      } catch (error) {
        console.error('Error initializing chat:', error);
        setError('Error initializing chat. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    initializeChat();
  }, []);

  // Persist the active thread to localStorage whenever it changes
  useEffect(() => {
    if (activeThreadId) {
      localStorage.setItem('lastActiveThreadId', activeThreadId);
    }
  }, [activeThreadId]);

  return {
    query,
    setQuery,
    selectedCitation,
    setSelectedCitation,
    threads,
    activeThreadId,
    threadMessages,
    isLoading,
    error,
    handleSelectThread,
    handleNewThread,
    handleDeleteThread,
    handleTemplateClick,
    handleQuerySubmit,
  };
}

export default useChat;
